import {
  Component,
  inject,
  OnInit,
  DestroyRef,
  effect,
  Injector,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { Title } from '@angular/platform-browser';
import { SwUpdate } from '@angular/service-worker';
import { AppService } from '@core/services/app.service';
import { LanguageStoreService } from '@core/stores/language-store.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@core/services/dialog.service';
import { AuthStoreService } from '@core/auth/auth-store.service';
import { ErrorStateService } from '@core/error/error-state-store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeService } from '@core/services/theme.service';
import { DOCUMENT } from '@angular/common';
import { AssetMonitorStoreService } from '@core/services/assert-monitor-store.service';
import { AppDynamicsService } from '@root/app/core/services/app-dynamics.service';

@Component({
  selector: 'app-root',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  protected readonly appService = inject(AppService);
  private readonly assetMonitorStoreService = inject(AssetMonitorStoreService);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly themeService = inject(ThemeService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly translateService = inject(TranslateService);
  private readonly titleService = inject(Title);
  private readonly dialogService = inject(DialogService);
  title = 'Security Operations System';
  private readonly languageStoreService = inject(LanguageStoreService);
  private readonly matIconReg = inject(MatIconRegistry);
  private readonly authStoreService = inject(AuthStoreService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly $initAuthInProgress =
    this.authStoreService.$initAuthInProgress;
  protected readonly $languageChangeInProgress =
    this.languageStoreService.$languageChangeInProgress;
  private readonly errorStateService = inject(ErrorStateService);
  private readonly appDynamicsService = inject(AppDynamicsService);
  constructor() {
    this.languageStoreService.initTranslation();
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined');
    // Initialize adrum (App dynamics) service to ensure it loads conditionally
    this.appDynamicsService.initializeAdrum();
  }

  ngOnInit() {
    this.translateService
      .get('appTitle')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translated: string) => {
        this.titleService.setTitle(translated);
      });
    // Only schedule the process when user is logged in
    if (this.swUpdate.isEnabled && this.authStoreService.$isAuthenticated()) {
      setInterval(() => {
        // If there was one of the defined error states don't fetch the new config file
        if (this.errorStateService.$errorState()) return;
        this.swUpdate.checkForUpdate().then(async (isUpdateAvailable) => {
          if (isUpdateAvailable) {
            const confirm = await this.dialogService.showConfirmDialog({
              title: this.translateService.instant('new-deploy.title'),
              subTitle: this.translateService.instant('new-deploy.subtitle'),
            });
            if (confirm) {
              window.location.reload();
            }
          }
        });
      }, 60000);
    }

    effect(
      () => {
        if (this.assetMonitorStoreService.$fontLoaded()) {
          this.document.body.classList.add('font-loaded');
        }
      },
      {
        injector: this.injector,
      }
    );
    effect(
      () => {
        if (this.themeService.$theme() === 'dark') {
          this.document.body.classList.add('dark-theme');
        } else {
          this.document.body.classList.remove('dark-theme');
        }
      },
      {
        injector: this.injector,
      }
    );
  }
}
